import React, { FC, Fragment } from 'react';
import { Typography, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import {
  EmptyState,
  Loaders,
  DataTable,
  DataTableActions,
  DataTableColumn,
  CustomSVG,
  ICustomSVG,
  PostImage,
} from 'components';
import { SharesItemFragment } from 'api';
import { useNotify, useSocialConnections } from 'hooks';
import { useSharesItems } from 'features/activity/hooks';
import { getSocialAccount } from 'features/activity/helpers';

import ListLoader from '../ListLoader';
import Actions from '../Actions';
import { isUrl } from 'features/content/helpers';
import UrlMetadata from './UrlMetadata';
import Body from './Body';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
  },
  twitter: {
    color: theme.custom.networks.twitter,
  },
  linkedin: {
    color: theme.custom.networks.linkedin,
  },
  socialAccount: {
    fontWeight: theme.typography.fontWeightBold,
  },
  userCommentary: {
    color: theme.palette.grey[600],
    ...theme.mixins.clamp(3),
  },
  date: {
    fontWeight: theme.typography.fontWeightBold,
    padding: '16px 0 16px 16px',
  },
  postCopy: {
    color: theme.palette.grey[600],
  },
  tableBodyRow: {
    height: 136,
  },
}));

const ScheduledShares: FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const { t } = useTranslation();
  const { connections } = useSocialConnections();

  const { results, isExhausted, isEmpty, isInitialLoad, sentinelRef } = useSharesItems(
    {
      scheduled: true,
      group_by_session: false,
      data: {
        limit: 25,
      },
    },
    {
      onError: notify.queryError,
    }
  );

  if (isInitialLoad) {
    return <ListLoader />;
  }

  if (isEmpty) {
    return <EmptyState name="scheduled" />;
  }

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const columns: DataTableColumn<SharesItemFragment>[] = [
    {
      width: 170,
      headerCell: {
        render: t('common:scheduled'),
      },
      bodyCell: {
        padding: 'none',
        render: shareItem => (
          <Typography className={classes.date}>
            {moment.unix(shareItem.share_at).format('ddd MMM Do hh:mm a')}
          </Typography>
        ),
      },
    },
    {
      width: 500,
      headerCell: {
        render: t('common:post'),
      },
      bodyCell: {
        render: shareItem => (
          <Grid container direction="row" wrap="nowrap" spacing={4}>
            <PostImage content={shareItem.content} />
            <Grid item>
              {isUrl(shareItem.content) ? (
                <UrlMetadata content={shareItem.content} classes={{ postCopy: classes.postCopy }} />
              ) : (
                <Body content={shareItem.content} />
              )}
            </Grid>
          </Grid>
        ),
      },
    },
    {
      width: 75,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        padding: 'none',
        render: shareItem => (
          <Grid container justify="center">
            <CustomSVG
              classes={{ root: classes[shareItem.network] }}
              name={shareItem.network as keyof ICustomSVG}
            />
          </Grid>
        ),
      },
    },
    {
      width: 395,
      headerCell: {
        render: t('components:shareCopy'),
      },
      bodyCell: {
        render: shareItem => (
          <Fragment>
            <Typography className={classes.socialAccount}>
              {getSocialAccount(shareItem, connections)}
            </Typography>
            <em>
              <Typography className={classes.userCommentary}>
                {shareItem.user_commentary}
              </Typography>
            </em>
          </Fragment>
        ),
      },
    },
  ];

  const actions: DataTableActions<SharesItemFragment> = shareItem => {
    return <Actions shareItem={shareItem} />;
  };

  return (
    <Fragment>
      <DataTable
        data={results}
        columns={columns}
        actions={actions}
        pagination={pagination}
        classes={{ tableBodyRow: classes.tableBodyRow }}
        rowSelection={null}
      />
      {!isExhausted && (
        <div ref={sentinelRef}>
          <Loaders.Standard />
        </div>
      )}
    </Fragment>
  );
};

export default ScheduledShares;
